import React, { useState, useEffect } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import DateRangeFilter from '../advanced-reporting/components/DateRangeFilter';
import ClientFilters from './components/ClientFilters';
import ClientRevenueTable from './components/ClientRevenueTable';
import RepeatServicePanel from './components/RepeatServicePanel';
import RevenueDistributionChart from './components/RevenueDistributionChart';
import ServiceFrequencyChart from './components/ServiceFrequencyChart';
import TopClientsPanel from './components/TopClientsPanel';
import EmailCommunicationLog from './components/EmailCommunicationLog';
import { clientAnalyticsService } from '../../services/clientAnalyticsService';
import { trackCampaignAttribution, trackRevenueConversion, trackUserBehaviorFunnel } from '../../utils/analytics';

const ClientAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('last30days');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filters, setFilters] = useState({
    clientType: 'all',
    minRevenue: ''
  });
  const [analyticsData, setAnalyticsData] = useState({
    clientRevenue: [],
    repeatServiceRate: {},
    revenueDistribution: [],
    serviceFrequency: [],
    topClients: []
  });

  useEffect(() => {
    // Initialize with last 30 days
    const today = new Date();
    const last30 = new Date(today);
    last30?.setDate(last30?.getDate() - 30);
    
    setDateRange({
      startDate: last30?.toISOString()?.split('T')?.[0],
      endDate: today?.toISOString()?.split('T')?.[0]
    });
  }, []);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      loadAnalyticsData();
    }
  }, [dateRange, filters]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const filterParams = {
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        clientType: filters?.clientType,
        minRevenue: filters?.minRevenue
      };

      const [clientRevenue, repeatServiceRate, revenueDistribution, serviceFrequency, topClients] = await Promise.all([
        clientAnalyticsService?.getClientRevenueData(filterParams),
        clientAnalyticsService?.getRepeatServiceRate(filterParams),
        clientAnalyticsService?.getClientDistributionByRevenue(filterParams),
        clientAnalyticsService?.getServiceFrequencyPatterns(filterParams),
        clientAnalyticsService?.getTopValueClients(10, filterParams)
      ]);

      setAnalyticsData({
        clientRevenue,
        repeatServiceRate,
        revenueDistribution,
        serviceFrequency,
        topClients
      });

      // Track revenue attribution
      const totalRevenue = clientRevenue?.reduce((sum, client) => sum + (client?.totalRevenue || 0), 0);
      const totalClients = clientRevenue?.length;
      
      trackCampaignAttribution({
        total_revenue: totalRevenue,
        total_clients: totalClients,
        repeat_rate: repeatServiceRate?.repeatRate,
        date_range: `${dateRange?.startDate}_to_${dateRange?.endDate}`,
        client_type_filter: filters?.clientType,
        attribution_source: 'email_automation'
      });

      // Track revenue conversions for top clients
      topClients?.slice(0, 5)?.forEach((client, index) => {
        trackRevenueConversion({
          value: client?.totalRevenue,
          client_id: client?.clientId,
          client_name: client?.clientName,
          service_count: client?.serviceCount,
          client_lifetime_value: client?.clientLifetimeValue,
          client_rank: index + 1,
          transaction_id: `client_${client?.clientId}_${dateRange?.startDate}`
        });
      });

      // Track funnel step
      trackUserBehaviorFunnel('client_analytics_viewed', {
        funnel_stage: 'view_revenue_attribution',
        total_clients: totalClients,
        total_revenue: totalRevenue,
        repeat_service_rate: repeatServiceRate?.repeatRate
      });
    } catch (error) {
      console.error('Error loading analytics data:', error);
      setAnalyticsData({
        clientRevenue: [],
        repeatServiceRate: {},
        revenueDistribution: [],
        serviceFrequency: [],
        topClients: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDateRange) => {
    setDateRange(prev => ({ ...prev, ...newDateRange }));
  };

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
  };

  const handleFilterChange = (newFilters) => {
    trackUserBehaviorFunnel('client_filters_applied', {
      funnel_stage: 'filter_client_data',
      filters: newFilters
    });
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      
      <main className="flex-1 overflow-hidden ml-0 lg:ml-64">
        <div className="h-full overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  Análisis de Clientes
                </h1>
                <p className="text-sm text-muted-foreground">
                  Inteligencia de clientes con análisis de ingresos y métricas de relación
                </p>
              </div>
              <Icon name="Users" size={32} className="text-primary" />
            </div>

            {/* Filters */}
            <div className="space-y-4">
              <DateRangeFilter
                startDate={dateRange?.startDate}
                endDate={dateRange?.endDate}
                onDateChange={handleDateChange}
                preset={preset}
                onPresetChange={handlePresetChange}
              />
              <ClientFilters
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </div>

            {/* Main Revenue Table */}
            <ClientRevenueTable
              data={analyticsData?.clientRevenue}
              loading={loading}
            />

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RepeatServicePanel
                data={analyticsData?.repeatServiceRate}
                loading={loading}
              />
              <TopClientsPanel
                data={analyticsData?.topClients}
                loading={loading}
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RevenueDistributionChart
                data={analyticsData?.revenueDistribution}
                loading={loading}
              />
              <ServiceFrequencyChart
                data={analyticsData?.serviceFrequency}
                loading={loading}
              />
            </div>

            {/* Email Communication Log Section */}
            <EmailCommunicationLog
              dateRange={dateRange}
              loading={loading}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientAnalytics;